import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useScrollSnap } from '../../hooks/useScrollSnap';
import {
    IonPage,
    IonContent,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonList,
    IonItem,
    IonLabel,
    IonIcon,
    IonButton,
    IonText,
    IonBadge,
    IonGrid,
    IonToggle,
    IonModal,
    IonAlert,
    IonToast,
} from '@ionic/react';
import {
    fingerPrint,
    shield,
    key,
    lockClosed,
    mail,
    checkmarkCircle,
    alertCircle,
    phonePortrait,
    notifications,
    eye,
    warning,
    closeOutline,
    search,
} from 'ionicons/icons';
import { PasskeyManagement } from '../../components/PasskeyManagement';
import { rsAuth } from '@rsmaids/shared/services/rsAuthClient';
import BasePageWithHeader, { TitleCard } from '../../components/BasePageWithHeader';
import styles from './Security.module.scss';
import SearchModal from '../../components/SearchModal';
import { useTranslation } from 'react-i18next';

interface SecurityProps {
    onClose?: () => void;
}

export const Security: React.FC<SecurityProps> = ({ onClose }) => {
  const { t } = useTranslation();
    // Refs for scroll animation
    const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
    const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
    const containerRef = useRef<HTMLIonGridElement>(null);

    const [showPasskeyModal, setShowPasskeyModal] = useState(false);
    const [showSearchModal, setShowSearchModal] = useState(false);
    const [emailVerified] = useState(true);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [loginNotifications, setLoginNotifications] = useState(true);
    const [passkeyCount, setPasskeyCount] = useState<number>(0);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const hasStrongPassword = true;

    const completedSecurityChecks = [emailVerified, hasStrongPassword, twoFactorEnabled].filter(Boolean).length;
    const securityStatusByChecks = ['At risk', 'Partially protected', 'Protected', 'Fully protected'] as const;
    const securityStatus = securityStatusByChecks[completedSecurityChecks];

    const handleChangePassword = useCallback(async (data: {
        currentPassword?: string;
        newPassword?: string;
        confirmPassword?: string;
    }) => {
        const currentPassword = (data.currentPassword || '').trim();
        const newPassword = (data.newPassword || '').trim();
        const confirmPassword = (data.confirmPassword || '').trim();

        if (!currentPassword || !newPassword || !confirmPassword) {
            setToastMessage(t('pages.Security.Security.pleaseFillInAllPasswordFields'));
            return;
        }
        if (newPassword.length < 6) {
            setToastMessage(t('pages.Security.Security.newPasswordMustBeAtLeast'));
            return;
        }
        if (newPassword !== confirmPassword) {
            setToastMessage(t('pages.Security.Security.newPasswordsDoNotMatch'));
            return;
        }
        if (newPassword === currentPassword) {
            setToastMessage(t('pages.Security.Security.newPasswordMustBeDifferentFrom'));
            return;
        }

        try {
            const result = await rsAuth.changePassword(currentPassword, newPassword);
            if (result.success) {
                setShowChangePassword(false);
                setToastMessage(t('pages.Security.Security.passwordChangedSuccessfully'));
            } else {
                setToastMessage(result.error || t('pages.Security.Security.failedToChangePassword'));
            }
        } catch (err) {
            console.error('[Security] Change password error:', err);
            setToastMessage(t('pages.Security.Security.anErrorOccurredWhileChangingYour'));
        }
    }, [t]);

    // Load passkey count on mount
    useEffect(() => {
        const loadPasskeyCount = async () => {
            try {
                const result = await rsAuth.listUserPasskeys();
                if (result.success && result.passkeys) {
                    setPasskeyCount(result.passkeys.length);
                }
            } catch (err) {
                console.error('Failed to load passkey count:', err);
            }
        };
        loadPasskeyCount();
    }, []);

    const handleModalDismiss = useCallback(() => {
        setShowPasskeyModal(false);
    }, []);

    const applyProgress = useCallback((progress: number, _smooth: boolean) => {
        baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
        if (titleCardAnimationRef.current) {
            titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
            titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
        }
    }, []);

    const handleScroll = useScrollSnap(containerRef, 66, applyProgress);

    const pageContent = (
        <BasePageWithHeader
            ref={baseHeaderRef}
            title={t('pages.Security.Security.security')}
            toolbarClassName={styles.toolbar}
            smallTitleClassName={styles.smallTitle}
            titleHeaderClassName={styles.titleHeader}
            startButtons={
                <IonButton shape='round' className={styles.closeButton} onClick={onClose}>
                    <IonIcon icon={closeOutline} />
                </IonButton>
            }
            endButtons={
                <IonButton shape='round' className={styles.searchButton} onClick={() => setShowSearchModal(true)}>
                    <IonIcon icon={search} />
                </IonButton>
            }
        >
            <IonContent scrollY={false} className={styles.content}>
                <IonGrid
                    ref={containerRef}
                    className={styles.securityContainer}
                    onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop)}
                >
                    <TitleCard
                        ref={titleCardAnimationRef}
                        title={t('pages.Security.Security.security')}
                        cardClassName={styles.securityScoreCard}
                        titleContainerClassName={styles.titleContainer}
                        titleClassName={styles.securityTitle}
                    />

                    <IonCard className={styles.sectionCard}>
                        <IonCardContent className={styles.securitySummaryCardBody}>
                        <div className={styles.heroCompact}>
                            <div className={styles.scoreCircle}>
                                <IonIcon icon={shield} className={styles.scoreIcon} />
                            </div>
                            <div className={styles.identityBlock}>
                                <div className={styles.scoreHeaderRow}>
                                    <h2 className={styles.scoreSubtitle}>{t('pages.Security.Security.securityStatus')}</h2>
                                    <span className={styles.scoreValue}>{securityStatus}</span>
                                </div>
                                <p className={styles.scoreDescription}>{t('pages.Security.Security.yourAccountIsWellProtected')}
                                </p>
                            </div>
                        </div>

                        {/* Security Checklist */}
                        <div className={styles.securityChecklist}>
                            <div className={styles.checklistItem}>
                                <IonIcon
                                    icon={emailVerified ? checkmarkCircle : alertCircle}
                                    color={emailVerified ? 'success' : 'warning'}
                                    className={styles.checklistIcon}
                                />
                                <IonText>
                                    <p className={styles.checklistText}>{t('pages.Security.Security.emailVerified')}</p>
                                </IonText>
                            </div>
                            <div className={styles.checklistItem}>
                                <IonIcon
                                    icon={hasStrongPassword ? checkmarkCircle : alertCircle}
                                    color={hasStrongPassword ? 'success' : 'medium'}
                                    className={styles.checklistIcon}
                                />
                                <IonText>
                                    <p className={styles.checklistText}>{t('pages.Security.Security.strongPassword')}</p>
                                </IonText>
                            </div>
                            <div className={styles.checklistItem}>
                                <IonIcon
                                    icon={twoFactorEnabled ? checkmarkCircle : alertCircle}
                                    color={twoFactorEnabled ? 'success' : 'medium'}
                                    className={styles.checklistIcon}
                                />
                                <IonText>
                                    <p className={styles.checklistText}>{t('pages.Security.Security.twoFactorAuthentication')} {!twoFactorEnabled && '(Recommended)'}
                                    </p>
                                </IonText>
                            </div>
                        </div>
                        </IonCardContent>
                    </IonCard>

                    {/* Passkey Authentication */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardHeader>
                            <IonCardTitle className={styles.sectionTitle}>
                                <IonIcon icon={fingerPrint} className={styles.sectionIcon} />{t('pages.Security.Security.passkeyAuthentication')}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonText color="medium">
                                <p className={styles.sectionDescription}>{t('pages.Security.Security.useYourDeviceSBiometricsFingerprint')}
                                </p>
                            </IonText>
                            <IonList className={styles.settingsList}>
                                <IonItem
                                    button
                                    detail
                                    onClick={() => setShowPasskeyModal(true)}
                                    className={styles.settingsItem}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                        <IonIcon icon={phonePortrait} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.managePasskeys')}</h3>
                                        <p>{t('pages.Security.Security.addOrRemoveDevicesForPasswordless')}</p>
                                    </IonLabel>
                                    <IonBadge slot="end" color="primary">{passkeyCount}</IonBadge>
                                </IonItem>
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Password & Authentication */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardHeader>
                            <IonCardTitle className={styles.sectionTitle}>
                                <IonIcon icon={lockClosed} className={styles.sectionIcon} />{t('pages.Security.Security.passwordAuthentication')}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonList className={styles.settingsList}>
                                <IonItem
                                    button
                                    detail
                                    className={styles.settingsItem}
                                    onClick={() => setShowChangePassword(true)}
                                >
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                        <IonIcon icon={key} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.changePassword')}</h3>
                                        <p>{t('pages.Security.Security.updateYourAccountPassword')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem button detail className={styles.settingsItem}>
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                        <IonIcon icon={mail} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.emailVerification')}</h3>
                                        <p>
                                            {emailVerified ? t('pages.Security.Security.verified') : t('pages.Security.Security.verifyYourEmailAddress')}
                                        </p>
                                    </IonLabel>
                                    {emailVerified && (
                                        <IonIcon icon={checkmarkCircle} slot="end" color="success" />
                                    )}
                                </IonItem>

                                <IonItem lines="none" className={styles.settingsItem}>
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeTertiary}`}>
                                        <IonIcon icon={shield} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.twoFactorAuthentication')}</h3>
                                        <p>{t('pages.Security.Security.addAnExtraLayerOfSecurity')}</p>
                                    </IonLabel>
                                    <IonToggle
                                        checked={twoFactorEnabled}
                                        onIonChange={(e) => setTwoFactorEnabled(e.detail.checked)}
                                        slot="end"
                                    />
                                </IonItem>
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Security Activity */}
                    <IonCard className={styles.sectionCard}>
                        <IonCardHeader>
                            <IonCardTitle className={styles.sectionTitle}>
                                <IonIcon icon={eye} className={styles.sectionIcon} />{t('pages.Security.Security.securityActivity')}
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <IonList className={styles.settingsList}>
                                <IonItem button detail className={styles.settingsItem}>
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgePrimary}`}>
                                        <IonIcon icon={phonePortrait} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.activeSessions')}</h3>
                                        <p>{t('pages.Security.Security.manageDevicesWhereYouReSigned')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem button detail className={styles.settingsItem}>
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeWarning}`}>
                                        <IonIcon icon={warning} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.loginHistory')}</h3>
                                        <p>{t('pages.Security.Security.viewRecentSignInActivity')}</p>
                                    </IonLabel>
                                </IonItem>

                                <IonItem lines="none" className={styles.settingsItem}>
                                    <div slot="start" className={`${styles.iconBadge} ${styles.iconBadgeSecondary}`}>
                                        <IonIcon icon={notifications} />
                                    </div>
                                    <IonLabel>
                                        <h3>{t('pages.Security.Security.loginNotifications')}</h3>
                                        <p>{t('pages.Security.Security.alertMeAboutNewSignIns')}</p>
                                    </IonLabel>
                                    <IonToggle
                                        checked={loginNotifications}
                                        onIonChange={(e) => setLoginNotifications(e.detail.checked)}
                                        slot="end"
                                    />
                                </IonItem>
                            </IonList>
                        </IonCardContent>
                    </IonCard>

                    {/* Security Tips */}
                    <IonCard className={styles.tipsCard}>
                        <IonCardContent>
                            <IonText>
                                <h3 className={styles.tipsTitle}>
                                    <IonIcon icon={shield} />{t('pages.Security.Security.securityTips')}
                                </h3>
                                <ul className={styles.tipsList}>
                                    <li>{t('pages.Security.Security.usePasskeysForTheMostSecure')}</li>
                                    <li>{t('pages.Security.Security.neverShareYourPasswordOrPasskey')}</li>
                                    <li>{t('pages.Security.Security.enableTwoFactorAuthenticationForExtra')}</li>
                                    <li>{t('pages.Security.Security.reviewYourActiveSessionsRegularly')}</li>
                                    <li>{t('pages.Security.Security.useAUniqueStrongPasswordFor')}</li>
                                </ul>
                            </IonText>
                        </IonCardContent>
                    </IonCard>
                </IonGrid>
            </IonContent>

            {/* Passkey Management Modal */}
            <IonModal
                isOpen={showPasskeyModal}
                onDidDismiss={handleModalDismiss}
                onWillDismiss={handleModalDismiss}
                canDismiss={true}
                backdropDismiss={true}
                keepContentsMounted={false}
                handle={false}
            >
                <PasskeyManagement
                    onClose={() => setShowPasskeyModal(false)}
                    onPasskeysChange={setPasskeyCount}
                />
            </IonModal>
            <SearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
            />

            {/* Change password */}
            <IonAlert
                isOpen={showChangePassword}
                cssClass="glass-alert"
                header={t('pages.Security.Security.changePassword')}
                inputs={[
                    { name: 'currentPassword', type: 'password', placeholder: t('pages.Security.Security.currentPassword') },
                    { name: 'newPassword', type: 'password', placeholder: t('pages.Security.Security.newPasswordMin6Characters') },
                    { name: 'confirmPassword', type: 'password', placeholder: t('pages.Security.Security.confirmNewPassword') },
                ]}
                buttons={[
                    { text: t('pages.Security.Security.cancel'), role: 'cancel', handler: () => setShowChangePassword(false) },
                    {
                        text: t('pages.Security.Security.update'),
                        handler: (data) => {
                            // Return false to keep the alert open; we manage dismissal ourselves
                            handleChangePassword(data);
                            return false;
                        },
                    },
                ]}
                onDidDismiss={() => setShowChangePassword(false)}
            />

            <IonToast
                isOpen={!!toastMessage}
                message={toastMessage}
                duration={2500}
                onDidDismiss={() => setToastMessage('')}
            />
        </BasePageWithHeader>
    );

    return onClose ? <div className={styles.modalWrapper}>{pageContent}</div> : <IonPage className={styles.page}>{pageContent}</IonPage>;
};
